import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { previewPiece } from '../canvas-geometry';
import { RoomOutline } from '../room-outlines/rooms';
import { GeneratedTable } from './layout-generator';

/**
 * TVG-144: a small, non-interactive mini-view of a layout on its room outline.
 *
 * Used both as a gallery thumbnail (compare layouts at a glance) and, larger, in
 * the expanded preview. It shares the canvas' geometry (room outline, trestle
 * rects and chairs, TVG-373 true size) so a preview reads as the same drawing
 * that opens on the live canvas, but carries none of the drag machinery.
 */
@Component({
  selector: 'app-layout-preview',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <svg
      class="h-full w-full"
      [attr.viewBox]="viewBox"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      [attr.aria-label]="ariaLabel">
      <path [attr.d]="outline.fillPath" class="tp-prev-room" />
      @for (t of tables; track $index) {
        @let piece = pieceFor(t);
        <g [attr.transform]="piece.transform">
          @for (rect of piece.trestles; track $index) {
            <rect
              [attr.x]="rect.x"
              [attr.y]="rect.y"
              [attr.width]="rect.w"
              [attr.height]="rect.h"
              rx="6"
              [class]="t.is_top_table ? 'tp-prev-top' : 'tp-prev-table'" />
          }
          @for (chair of piece.chairs; track $index) {
            <rect
              [attr.x]="chair.x"
              [attr.y]="chair.y"
              [attr.width]="chair.w"
              [attr.height]="chair.h"
              rx="8"
              class="tp-prev-chair" />
          }
        </g>
      }
    </svg>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100%;
      }
      .tp-prev-room {
        fill: #ffffff;
        stroke: #2a332e;
        stroke-width: 12;
      }
      .tp-prev-table,
      .tp-prev-top {
        fill: #e3ece6;
        stroke: #3f6b55;
        stroke-width: 4;
      }
      .tp-prev-chair {
        fill: #ffffff;
        stroke: #9aa3b2;
        stroke-width: 3;
      }
      :host-context(.dark) .tp-prev-room {
        fill: #0f172a;
        stroke: #64748b;
      }
    `,
  ],
})
export class LayoutPreviewComponent {
  @Input({ required: true }) public outline!: RoomOutline;
  @Input({ required: true }) public tables: GeneratedTable[] = [];
  @Input() public ariaLabel = 'Layout preview';

  private readonly MARGIN = 60;

  protected get viewBox(): string {
    const o = this.outline;
    return `${-this.MARGIN} ${-this.MARGIN} ${o.width + 2 * this.MARGIN} ${o.height + 2 * this.MARGIN}`;
  }

  protected pieceFor(table: GeneratedTable) {
    return previewPiece(table);
  }
}
